import { useState, useEffect, useCallback } from 'react';
import { Building2, Loader2, X, Plus } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { userService } from '@/services/user.service';
import { SystemNotificationDepartmentRole } from '@/types/notification.types';
import toast from 'react-hot-toast';

interface DepartmentOption {
  group_key: string;
  group_name: string;
}

interface RoleOption {
  role_key: string;
  role_name: string;
}

// Flat pair while editing; grouped back into department_roles on save.
interface SelectedRole {
  group_key: string;
  group_name: string;
  role_key: string;
  role_name: string;
}

interface DepartmentRolePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventLabel: string;
  value: SystemNotificationDepartmentRole[];
  onSave: (department_roles: SystemNotificationDepartmentRole[]) => void;
}

export function DepartmentRolePickerModal({
  isOpen,
  onClose,
  eventLabel,
  value,
  onSave,
}: DepartmentRolePickerModalProps) {
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [selected, setSelected] = useState<SelectedRole[]>([]);

  const [departmentKey, setDepartmentKey] = useState('');
  const [roleKey, setRoleKey] = useState('');

  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // Saved config only stores keys, so names are resolved from the department
  // list once it arrives.
  const hydrateSelected = useCallback(
    async (department_options: DepartmentOption[]) => {
      const hydrated: SelectedRole[] = [];

      for (const entry of value) {
        const department = department_options.find((d) => d.group_key === entry.group_key);
        let role_options: RoleOption[] = [];

        try {
          const response = await userService.getGroupRoleMap(entry.group_key);
          role_options = response?.data?.roles || [];
        } catch {
          role_options = [];
        }

        for (const role_key of entry.role_keys) {
          hydrated.push({
            group_key: entry.group_key,
            group_name: department?.group_name || entry.group_key,
            role_key,
            role_name: role_options.find((r) => r.role_key === role_key)?.role_name || role_key,
          });
        }
      }

      setSelected(hydrated);
    },
    [value]
  );

  useEffect(() => {
    if (!isOpen) return;

    setDepartmentKey('');
    setRoleKey('');
    setRoles([]);

    (async () => {
      setIsLoadingDepartments(true);
      let department_options: DepartmentOption[] = [];
      try {
        const response = await userService.getGroupsDropdown();
        department_options = response?.data || [];
        setDepartments(department_options);
      } catch {
        toast.error('Failed to load departments');
        setDepartments([]);
      } finally {
        setIsLoadingDepartments(false);
      }

      await hydrateSelected(department_options);
    })();
  }, [isOpen, hydrateSelected]);

  // Roles only exist inside a department, so the list reloads on every change.
  useEffect(() => {
    setRoleKey('');
    setRoles([]);

    if (!departmentKey) return;

    let cancelled = false;
    (async () => {
      setIsLoadingRoles(true);
      try {
        const response = await userService.getGroupRoleMap(departmentKey);
        if (!cancelled) setRoles(response?.data?.roles || []);
      } catch {
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setIsLoadingRoles(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [departmentKey]);

  const handleAdd = () => {
    if (!departmentKey || !roleKey) return;

    const exists = selected.some((s) => s.group_key === departmentKey && s.role_key === roleKey);
    if (exists) {
      toast.error('That role is already selected');
      return;
    }

    const department = departments.find((d) => d.group_key === departmentKey);
    const role = roles.find((r) => r.role_key === roleKey);
    if (!role) return;

    setSelected((prev) => [
      ...prev,
      {
        group_key: departmentKey,
        group_name: department?.group_name || departmentKey,
        role_key: role.role_key,
        role_name: role.role_name,
      },
    ]);
    setRoleKey('');
  };

  const handleRemove = (group_key: string, role_key: string) => {
    setSelected((prev) =>
      prev.filter((s) => !(s.group_key === group_key && s.role_key === role_key))
    );
  };

  const handleSave = () => {
    const grouped = new Map<string, string[]>();
    for (const entry of selected) {
      const existing = grouped.get(entry.group_key) || [];
      grouped.set(entry.group_key, [...existing, entry.role_key]);
    }

    onSave(
      Array.from(grouped.entries()).map(([group_key, role_keys]) => ({ group_key, role_keys }))
    );
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Departments & Roles"
      subtitle={`Everyone holding a selected role will be notified for ${eventLabel}`}
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {selected.length} role{selected.length === 1 ? '' : 's'} selected
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Apply Selection
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Department then role, because roles only exist inside a department */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Department
            </label>
            <select
              value={departmentKey}
              onChange={(e) => setDepartmentKey(e.target.value)}
              disabled={isLoadingDepartments}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            >
              <option value="">
                {isLoadingDepartments ? 'Loading...' : 'Select a department'}
              </option>
              {departments.map((department) => (
                <option key={department.group_key} value={department.group_key}>
                  {department.group_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Role
            </label>
            <select
              value={roleKey}
              onChange={(e) => setRoleKey(e.target.value)}
              disabled={!departmentKey || isLoadingRoles}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            >
              <option value="">
                {!departmentKey
                  ? 'Select a department first'
                  : isLoadingRoles
                  ? 'Loading...'
                  : roles.length === 0
                  ? 'No roles in this department'
                  : 'Select a role'}
              </option>
              {roles.map((role) => (
                <option key={role.role_key} value={role.role_key}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleAdd}
            disabled={!departmentKey || !roleKey}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        {/* Selected roles */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Selected Roles
          </p>

          {selected.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 border border-dashed border-gray-200 rounded-lg">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Building2 className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-0.5">No roles selected</p>
              <p className="text-xs text-gray-500 text-center max-w-xs">
                Pick a department, choose a role, then select Add. You can combine roles from
                several departments.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selected.map((entry) => (
                <span
                  key={`${entry.group_key}:${entry.role_key}`}
                  className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 text-sm bg-primary-50 text-primary-700 border border-primary-200 rounded-full"
                >
                  <span className="text-primary-500">{entry.group_name}</span>
                  <span className="text-primary-300">/</span>
                  <span className="font-medium">{entry.role_name}</span>
                  <button
                    onClick={() => handleRemove(entry.group_key, entry.role_key)}
                    className="p-0.5 hover:bg-primary-100 rounded-full transition-colors"
                    aria-label={`Remove ${entry.role_name}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {isLoadingDepartments && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading departments...
          </div>
        )}
      </div>
    </Modal>
  );
}

export default DepartmentRolePickerModal;
